import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import {
  useUsersApi,
  useTagsApi,
  DirectoryUnavailableError,
  type CreateUserDto,
  type ModuleGrantInput,
  type CreateUserResult,
  type DirectoryManager,
  type DirectoryUser,
} from '@/services/api';
import { EntraUserPicker } from './EntraUserPicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { TagBadge } from '@/components/shared/TagBadge';
import { PERMISSION_MODULES, USER_ROLE_NAMES } from '@ip/shared-types';
import type { CompanyListItem, PermissionModule, Tag, UserRoleName } from '@ip/shared-types';

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Companies the acting admin may assign roles/grants in — superadmin: all, company admin: only theirs. */
  companies: CompanyListItem[];
}

/** Mirrors UsersAdminService.TAG_FILTERABLE_MODULES on the backend. */
const TAG_FILTERABLE_MODULES = ['expense', 'sales'] as const;
type TagFilterableModule = (typeof TAG_FILTERABLE_MODULES)[number];

function isTagFilterableModule(module: PermissionModule): module is TagFilterableModule {
  return (TAG_FILTERABLE_MODULES as readonly string[]).includes(module);
}

type AuthProvider = 'entra' | 'local';

interface FormValues {
  authProvider: AuthProvider;
  email: string;
  displayName: string;
  /** Entra object id of the user picked from the directory ('' = manual entry). */
  entraOid: string;
  companyNip: string;
  role: UserRoleName;
  sendInvite: boolean;
}

type ManagerPreview = { status: 'loading' } | { status: 'loaded'; manager: DirectoryManager | null };

// Microsoft (Entra ID) is the default: no password is generated and the
// account links up automatically on the user's first Microsoft login.
// The notification email is opt-in for Entra, opt-out for local.
const EMPTY_FORM: FormValues = {
  authProvider: 'entra',
  email: '',
  displayName: '',
  entraOid: '',
  companyNip: '',
  role: 'user',
  sendInvite: false,
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      {children}
    </div>
  );
}

export function AddUserDialog({ open, onClose, onCreated, companies }: AddUserDialogProps) {
  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [selectedModules, setSelectedModules] = useState<Set<PermissionModule>>(new Set());
  const [tagsByModule, setTagsByModule] = useState<Partial<Record<TagFilterableModule, string[]>>>({});
  const [companyTags, setCompanyTags] = useState<Partial<Record<TagFilterableModule, Tag[]>>>({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<CreateUserResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Entra directory picker state. directoryAvailable flips to false on the
  // first 503 DIRECTORY_UNAVAILABLE (Graph permission not granted yet) and
  // the dialog falls back to the plain e-mail input; it resets on close so a
  // freshly granted permission is picked up on the next open.
  const [directoryAvailable, setDirectoryAvailable] = useState(true);
  const [pickedUser, setPickedUser] = useState<DirectoryUser | null>(null);
  const [managerPreview, setManagerPreview] = useState<ManagerPreview | null>(null);
  /** Guards the async manager fetch against a quick re-pick. */
  const pickSeq = useRef(0);

  const { createUser, getDirectoryUser } = useUsersApi();
  const { getTags } = useTagsApi();

  const setField = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const setProvider = (provider: AuthProvider) => {
    // Reset the invite flag to the provider's default: opt-in notification
    // for Entra, opt-out temp-password invite for local. A local account can
    // never carry a picked directory identity.
    setValues((prev) => ({
      ...prev,
      authProvider: provider,
      sendInvite: provider === 'local',
      ...(provider === 'local' ? { entraOid: '' } : {}),
    }));
    if (provider === 'local') {
      setPickedUser(null);
      setManagerPreview(null);
      pickSeq.current++;
    }
  };

  const handleDirectoryUnavailable = () => {
    // Keep whatever is already filled in — the admin continues manually.
    setDirectoryAvailable(false);
    setPickedUser(null);
    setManagerPreview(null);
    pickSeq.current++;
  };

  const handlePickDirectoryUser = async (user: DirectoryUser) => {
    setPickedUser(user);
    setValues((prev) => ({
      ...prev,
      email: user.email,
      displayName: user.displayName ?? prev.displayName,
      entraOid: user.entraOid,
    }));

    const seq = ++pickSeq.current;
    setManagerPreview({ status: 'loading' });
    try {
      const detail = await getDirectoryUser(user.entraOid);
      if (seq !== pickSeq.current) return;
      setManagerPreview({ status: 'loaded', manager: detail.manager });
    } catch (error) {
      if (seq !== pickSeq.current) return;
      if (error instanceof DirectoryUnavailableError) {
        handleDirectoryUnavailable();
        return;
      }
      console.error('Failed to load the manager preview:', error);
      setManagerPreview(null);
    }
  };

  // Load this company's expense/sales tags whenever the company changes and
  // the role is 'user' — tag-level filters are only meaningful for that role.
  useEffect(() => {
    if (!values.companyNip || values.role !== 'user') return;
    let mounted = true;
    Promise.all([getTags(values.companyNip, 'expense'), getTags(values.companyNip, 'sales')])
      .then(([expense, sales]) => {
        if (mounted) setCompanyTags({ expense, sales });
      })
      .catch(() => {
        if (mounted) setCompanyTags({});
      });
    return () => {
      mounted = false;
    };
  }, [values.companyNip, values.role, getTags]);

  const resetAndClose = () => {
    setValues(EMPTY_FORM);
    setSelectedModules(new Set());
    setTagsByModule({});
    setCompanyTags({});
    setResult(null);
    setCopied(false);
    setDirectoryAvailable(true);
    setPickedUser(null);
    setManagerPreview(null);
    pickSeq.current++;
    onClose();
  };

  const toggleModule = (module: PermissionModule) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  };

  const toggleTag = (module: TagFilterableModule, tagId: string) => {
    setTagsByModule((prev) => {
      const current = prev[module] ?? [];
      const next = current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId];
      return { ...prev, [module]: next };
    });
  };

  const canSubmit = values.email.trim().length > 0 && values.companyNip.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSaving(true);
      const modules: ModuleGrantInput[] | undefined =
        values.role === 'user'
          ? Array.from(selectedModules).map((module) => ({
              module,
              tagIds:
                isTagFilterableModule(module) && (tagsByModule[module]?.length ?? 0) > 0
                  ? tagsByModule[module]
                  : undefined,
            }))
          : undefined;

      const dto: CreateUserDto = {
        email: values.email.trim(),
        displayName: values.displayName.trim() || undefined,
        entraOid: values.authProvider === 'entra' && values.entraOid ? values.entraOid : undefined,
        role: values.role,
        companyNip: values.companyNip,
        authProvider: values.authProvider,
        modules,
        sendInvite: values.sendInvite,
      };

      const created = await createUser(dto);
      onCreated();

      if (created.temporaryPassword) {
        // Invite was skipped — hand the temp password to the admin instead
        // of closing, so it isn't lost.
        setResult(created);
      } else {
        toast.success(t('users.createSuccess'));
        resetAndClose();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.createFailed'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!result?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      toast.success(t('users.passwordCopied'));
    } catch (error) {
      console.error('Clipboard write failed:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('users.addUser')}</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <p className="text-sm">{t('users.createdNoInvite')}</p>
            <div className="bg-muted flex items-center justify-between gap-2 rounded-md border p-3">
              <div>
                <p className="text-muted-foreground text-xs">{t('users.temporaryPassword')}</p>
                <p className="font-mono text-sm font-medium">{result.temporaryPassword}</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleCopyPassword}>
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                {t('users.copyPassword')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label={t('users.form.accountType')}>
              <ToggleGroup
                type="single"
                variant="outline"
                className="w-full"
                value={values.authProvider}
                onValueChange={(v) => v && setProvider(v as AuthProvider)}
                aria-label={t('users.form.accountType')}
              >
                <ToggleGroupItem value="entra" className="flex-1">
                  {t('users.form.providerEntra')}
                </ToggleGroupItem>
                <ToggleGroupItem value="local" className="flex-1">
                  {t('users.form.providerLocal')}
                </ToggleGroupItem>
              </ToggleGroup>
            </Field>

            {values.authProvider === 'entra' && (
              <p className="text-muted-foreground text-xs">{t('users.form.entraHint')}</p>
            )}

            {values.authProvider === 'entra' && directoryAvailable ? (
              <Field label={t('users.form.directoryUser')}>
                <EntraUserPicker
                  value={pickedUser}
                  onSelect={handlePickDirectoryUser}
                  onUnavailable={handleDirectoryUnavailable}
                />
                {managerPreview?.status === 'loading' && (
                  <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <Loader2 className="size-3 animate-spin" />
                    {t('users.form.managerLoading')}
                  </p>
                )}
                {managerPreview?.status === 'loaded' && (
                  <p className="text-muted-foreground text-xs">
                    {managerPreview.manager
                      ? `${t('users.form.manager')}: ${managerPreview.manager.displayName ?? ''}${
                          managerPreview.manager.email ? ` <${managerPreview.manager.email}>` : ''
                        }`
                      : `${t('users.form.manager')}: ${t('users.form.noManager')}`}
                  </p>
                )}
              </Field>
            ) : (
              <>
                {values.authProvider === 'entra' && !directoryAvailable && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {t('users.form.directoryUnavailable')}
                  </p>
                )}
                <Field label={t('users.form.email')}>
                  <Input
                    type="email"
                    value={values.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="jan.kowalski@example.com"
                  />
                </Field>
              </>
            )}

            <Field label={t('users.form.displayName')}>
              <Input value={values.displayName} onChange={(e) => setField('displayName', e.target.value)} />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label={t('users.form.company')}>
                <Select value={values.companyNip} onValueChange={(v) => setField('companyNip', v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('users.form.selectCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.nip} value={c.nip}>
                        {c.name || c.nip}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={t('users.form.role')}>
                <Select value={values.role} onValueChange={(v) => setField('role', v as UserRoleName)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('users.form.selectRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_ROLE_NAMES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t(`users.roleNames.${role}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {values.role === 'user' && (
              <div className="space-y-2 rounded-md border p-3">
                <Label className="text-muted-foreground text-xs">{t('users.form.modules')}</Label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {PERMISSION_MODULES.map((module) => (
                    <label key={module} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={selectedModules.has(module)} onCheckedChange={() => toggleModule(module)} />
                      {t(`users.moduleNames.${module}`)}
                    </label>
                  ))}
                </div>

                {TAG_FILTERABLE_MODULES.filter((module) => selectedModules.has(module)).map((module) => (
                  <div key={module} className="space-y-1 pt-1">
                    <Label className="text-muted-foreground text-xs">
                      {t('users.form.tagsForModule', { module: t(`users.moduleNames.${module}`) })}
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {(companyTags[module] ?? []).length === 0 ? (
                        <p className="text-muted-foreground text-xs">{t('users.form.noTagsForModule')}</p>
                      ) : (
                        companyTags[module]?.map((tag) => (
                          <TagBadge
                            key={tag.id}
                            name={tag.name}
                            color={tag.color}
                            selected={(tagsByModule[module] ?? []).includes(tag.id)}
                            onClick={() => toggleTag(module, tag.id)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values.sendInvite}
                onCheckedChange={(checked) => setField('sendInvite', checked === true)}
              />
              {values.authProvider === 'entra'
                ? t('users.form.sendEntraNotice')
                : t('users.form.sendInvite')}
            </label>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={resetAndClose}>{t('users.close')}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={resetAndClose}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={saving || !canSubmit}>
                {saving && <Loader2 className="size-4 animate-spin" />}
                {t('common.add')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
